import { useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import { useBitcoinBalanceUSD } from '@/hooks/useBitcoinBalanceUSD';
import { Button } from '@/components/button';
import { Icons } from '@/components/icons';
import { formatWalletAddress } from '@/utils/formatWalletAddress';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { formatUsdValue } from '@/utils/formatUsdValue';
import { Wallets } from '@/constants/Wallets';
import { QueryKeys } from '@/hooks/api/query-keys';
import { cn } from '@/utils/cn';
import { formatBTC } from '@/utils/formatBTC';
import { useBitcoinBalance } from '@/hooks/useBitcoinBalance';
import { useRuneBalance } from '@/hooks/react/useRuneBalance';

import WalletConnectButton from './WalletConnectButton';

interface WalletConnectProps {
  openNetworkSettings: () => void;
}

const WalletConnect = ({ openNetworkSettings }: WalletConnectProps) => {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 1024px)'); // lg

  const { client, wallet, walletType } = useDucatClient();

  const walletBalanceBtc = useBitcoinBalance();
  const walletBalance = useBitcoinBalanceUSD();

  const { runeBalance } = useRuneBalance();

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  function onOpenDropdown() {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.walletBalance] });
  }

  if (wallet !== null) {
    const address = wallet.acct.sats.address;

    return (
      <DropdownMenu.Root
        open={isDropdownOpen}
        onOpenChange={(open) => {
          setIsDropdownOpen(open);
          if (open) {
            onOpenDropdown();
          }
        }}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            size='small'
            leadingIcon={walletType ? Wallets[walletType || WalletType.Xverse].logo : <></>}
            trailingIcon={<Icons.chevronDown className='hidden lg:block' />}
            variant='secondary'
            className='focus-visible:ring-1 focus-visible:ring-border-secondary [&_svg]:size-[18px] lg:[&_svg]:size-6 text-sm lg:text-base py-1.5 lg:py-2 px-2 lg:px-4 gap-1 lg:gap-2 leading-4 lg:leading-6'
          >
            {isMobile ? formatWalletAddress(address, 3, 3) : formatWalletAddress(address, 12, 5)}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={10}
            align='end'
            className={cn(
              'DropdownMenuContent relative lg:top-auto w-screen lg:w-[346px] h-screen lg:h-auto bg-surface-secondary ring-1 ring-border-secondary rounded-10 divide-y divide-border-primary z-50 overflow-hidden animate-fade-in',
              'top-[calc(100vh-476px)]',
            )}
          >
            <div className='relative flex lg:hidden justify-between items-center p-4'>
              <p className='text-[22px] lg:text-2xl font-bold text-text-primary'>Wallet Settings</p>
              <Button
                size='small'
                variant='tertiary'
                className='bg-transparent'
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                }}
              >
                <Icons.close />
              </Button>
              <div className='absolute top-0 left-1/2 transform -translate-x-1/2 w-9 h-[5px] bg-border-primary rounded-full mt-1.5' />
            </div>
            <div className='p-4 flex flex-col gap-2.5 select-none outline-none'>
              <p className='text-text-secondary'>Connected Wallet</p>
              <div className='flex items-center gap-4 w-full justify-between'>
                <div className='flex items-center gap-1'>
                  <span className='-ml-1 [&_svg]:size-6'>{walletType ? Wallets[walletType || WalletType.Xverse].logo : null}</span>
                  <span className='font-medium text-text-primary max-w-[200px] w-full'>{formatWalletAddress(address, 12, 5)}</span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(address, 'Address Copied');
                  }}
                  variant='tertiary'
                  className='bg-transparent'
                >
                  <Icons.copy width={16} height={16} className='text-text-primary' />
                </Button>
              </div>
            </div>
            <div className='p-4 flex flex-col gap-2.5 select-none outline-none'>
              <div className='flex justify-between items-center'>
                <p className='text-text-secondary'>Wallet balance</p>
                <span className='font-bold text-right text-text-primary'>
                  {/* TODO: Checkk later  */}
                  {walletBalance !== null ? (
                    walletBalance
                  ) : (
                    <div className='w-[75px] h-[14px] rounded-md overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_3s_infinite] before:bg-gradient-to-r before:from-transparent before:via-text-primary/30 before:to-transparent'></div>
                  )}
                </span>
              </div>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center justify-between w-full'>
                  <p className='flex items-center gap-x-1 text-text-secondary text-sm'>
                    <Icons.bitcoin /> Bitcoin
                  </p>
                  {/* TODO: Use reusable component here */}
                  <div className='text-text-secondary text-sm text-right min-h-3.5 flex flex-col items-end'>
                    {walletBalance !== null ? (
                      <>
                        {walletBalance}
                        <div className='hidden lg:block text-xs'>({formatBTC(walletBalanceBtc, 4)} BTC)</div>
                      </>
                    ) : (
                      <div className='w-[75px] h-[14px] rounded-md overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_3s_infinite] before:bg-gradient-to-r before:from-transparent before:via-text-primary/30 before:to-transparent'></div>
                    )}
                  </div>
                </div>
                <div className='flex items-center justify-between w-full'>
                  <p className='flex items-center gap-x-1 text-text-secondary text-sm'>
                    <Icons.unit /> UNIT
                  </p>
                  <span className='text-text-secondary text-sm text-right min-h-3.5'>
                    {/* {formatUsdValue(unitInVault, true)} */}
                    {formatUsdValue(runeBalance)}
                  </span>
                </div>
              </div>
            </div>
            <div className='p-4 select-none outline-none'>
              {/* TODO: avoid non-native interactive elements */}
              <div className='flex justify-between items-center cursor-pointer group' onClick={openNetworkSettings}>
                <p className='text-text-secondary group-hover:text-text-primary/75'>Network Settings</p>
                <Button variant='tertiary' className='bg-transparent p-0'>
                  <Icons.chevronRight className='text-text-secondary' />
                </Button>
              </div>
            </div>
            <div className='p-4 select-none outline-none block'>
              <button
                className='w-full ring-1 ring-red-100 text-red-100 py-1.5 text-center text-sm font-medium rounded'
                onClick={() => {
                  client.disconnect();
                  setIsDropdownOpen(false);
                }}
              >
                Disconnect Wallet
              </button>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    );
  }

  return <WalletConnectButton mobileStylesOn={true} />;
};

export default WalletConnect;
